import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBasket } from "@/context/BasketContext";
import { toast } from "sonner";
import { Minus, Plus, Trash2, ShoppingBag, Ticket, Tag, X, Lock } from "lucide-react";

function formatGBP(n) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 2 }).format(n);
}

export default function Basket() {
  const { user } = useAuth();
  const { items, updateTickets, removeItem, total } = useBasket();
  const [codeInput, setCodeInput] = useState("");
  const [applying, setApplying] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [discount, setDiscount] = useState(null); // {code, type, value, message}

  const checkout = async () => {
    setCheckingOut(true);
    try {
      const { data } = await api.post("/payments/nochex/basket/checkout", {
        items: items.map((it) => ({
          competition_id: it.competition_id,
          tickets: it.tickets,
          answer_token: it.answer_token,
        })),
        discount_code: discount?.code || null,
        origin_url: window.location.origin,
      });
      if (data.free) {
        window.location.href = data.url;
        return;
      }
      const form = document.createElement("form");
      form.method = "POST";
      form.action = data.url;
      Object.entries(data.form_fields).forEach(([k, v]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = k;
        input.value = v;
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setCheckingOut(false);
    }
  };

  const applyCode = async () => {
    if (!codeInput.trim()) return;
    setApplying(true);
    try {
      const { data } = await api.post("/discounts/validate", {
        code: codeInput.trim(),
        subtotal: Number(total.toFixed(2)),
      });
      if (data.valid) {
        setDiscount({ code: data.code, type: data.type, value: data.value, message: data.message });
        toast.success(data.message);
      } else {
        setDiscount(null);
        toast.error(data.message || "Invalid code");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setApplying(false);
    }
  };

  const removeCode = () => {
    setDiscount(null);
    setCodeInput("");
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-20 text-center" data-testid="basket-empty">
        <ShoppingBag className="mx-auto text-white/30" size={48} />
        <h1 className="font-display text-4xl text-white mt-4">Your basket is empty</h1>
        <p className="text-white/60 mt-2 text-sm">Answer a skill question on any raffle and add it to your basket.</p>
        <Link to="/competitions" className="btn-ufr-primary inline-block px-6 py-3 text-sm mt-6" data-testid="basket-browse-link">Browse raffles</Link>
      </div>
    );
  }

  let discountAmount = 0;
  if (discount) {
    discountAmount = discount.type === "percent" ? (total * discount.value) / 100 : Math.min(discount.value, total);
    discountAmount = Math.max(0, Math.round(discountAmount * 100) / 100);
  }
  const finalTotal = Math.max(0, Math.round((total - discountAmount) * 100) / 100);

  return (
    <div className="max-w-4xl mx-auto px-5 md:px-8 py-10 md:py-16" data-testid="basket-page">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--ufr-green)] font-bold">Checkout</div>
      <h1 className="font-display text-4xl md:text-5xl text-white mt-2 mb-8">Your basket</h1>

      <div className="space-y-4">
        {items.map((it) => (
          <div key={it.competition_id} className="ufr-card rounded-md p-4 flex items-center gap-4" data-testid={`basket-item-${it.competition_id}`}>
            <div className="w-16 h-20 rounded-sm overflow-hidden border border-white/10 shrink-0 bg-black/40">
              {it.image_url ? <img src={it.image_url} alt={it.title} className="w-full h-full object-cover" /> : null}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white font-bold truncate">{it.title}</div>
              <div className="text-white/50 text-xs mt-1 flex items-center gap-1"><Ticket size={12} /> {formatGBP(it.price_per_ticket)} per ticket</div>
            </div>
            <div className="flex items-center gap-2">
              <button className="btn-ufr-ghost w-8 h-8 flex items-center justify-center" onClick={() => updateTickets(it.competition_id, it.tickets - 1)} data-testid={`basket-dec-${it.competition_id}`}>
                <Minus size={12} />
              </button>
              <div className="font-display text-xl text-white w-8 text-center" data-testid={`basket-qty-${it.competition_id}`}>{it.tickets}</div>
              <button className="btn-ufr-ghost w-8 h-8 flex items-center justify-center" onClick={() => updateTickets(it.competition_id, it.tickets + 1)} data-testid={`basket-inc-${it.competition_id}`}>
                <Plus size={12} />
              </button>
            </div>
            <div className="text-right w-20">
              <div className="font-display text-lg text-[color:var(--ufr-green)]">{formatGBP(it.price_per_ticket * it.tickets)}</div>
            </div>
            <button className="text-white/40 hover:text-red-400 transition-colors" onClick={() => removeItem(it.competition_id)} data-testid={`basket-remove-${it.competition_id}`}>
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>

      <div className="ufr-card rounded-md p-6 mt-8">
        {/* Discount code */}
        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 font-bold mb-2 flex items-center gap-1"><Tag size={12} /> Discount code</div>
          {discount ? (
            <div className="flex items-center justify-between bg-[rgba(34,255,102,0.08)] border border-[color:var(--ufr-green)]/40 rounded-sm px-3 py-2.5" data-testid="discount-applied">
              <span className="text-sm text-[color:var(--ufr-green)] font-bold">{discount.code} applied</span>
              <button onClick={removeCode} className="text-white/50 hover:text-red-400" data-testid="discount-remove-btn"><X size={16} /></button>
            </div>
          ) : (
            <div className="flex gap-2">
              <input
                className="flex-1 bg-black/50 border border-white/15 rounded-sm px-3 py-2.5 text-sm text-white uppercase focus:border-[color:var(--ufr-green)] outline-none"
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && applyCode()}
                placeholder="Enter code"
                data-testid="discount-code-field"
              />
              <button onClick={applyCode} disabled={applying || !codeInput.trim()} className="btn-ufr-ghost px-5 py-2.5 text-sm" data-testid="apply-discount-btn">
                {applying ? "…" : "Apply"}
              </button>
            </div>
          )}
        </div>

        {discount && (
          <div className="space-y-1.5 mb-3 text-sm">
            <div className="flex items-center justify-between text-white/60">
              <span>Subtotal</span><span data-testid="basket-subtotal">{formatGBP(total)}</span>
            </div>
            <div className="flex items-center justify-between text-[color:var(--ufr-green)]">
              <span>Discount ({discount.code})</span><span data-testid="basket-discount">−{formatGBP(discountAmount)}</span>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 font-bold">Total ({items.length} raffle{items.length > 1 ? "s" : ""})</div>
          <div className="font-display text-3xl text-[color:var(--ufr-green)] glow-text" data-testid="basket-total">{formatGBP(finalTotal)}</div>
        </div>
        {!user && (
          <p className="text-yellow-300/80 text-xs mt-3">
            Please <Link to="/login?next=/basket" className="underline">login</Link> to complete your purchase.
          </p>
        )}
        <button
          onClick={checkout}
          disabled={!user || checkingOut}
          className="btn-ufr-primary w-full py-3 text-sm flex items-center justify-center gap-2 mt-5 disabled:opacity-50"
          data-testid="checkout-pay-btn"
        >
          <Lock size={14} /> {checkingOut ? "Redirecting to secure payment…" : "Pay securely by card"}
        </button>
        <p className="text-[11px] text-white/40 mt-3">You'll be redirected to Nochex, our secure payment provider. Skill required • 18+</p>
      </div>
    </div>
  );
}
